import { memo, useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, IconButton } from "@mui/material";
import { icon } from "@/app/style/icons";
import { useMutation, useQuery } from "@apollo/client";
import { DELETE_CHAT } from "@chat/graphql/mutations/deleteChat";
import { Mutation, MutationDeleteChatArgs, Query, Scalars } from "@/app/graphql/codegen";
import { FETCH_CHATS } from "@chat/graphql/queries/fetchChats";
import { FETCH_CHAT } from "@chat/graphql/queries/fetchChat";
import { TOGGLE_FAVORITE } from "@chat/graphql/mutations/toggleFavorite";
import { useDispatch } from "react-redux";
import { setSelectChat } from "@store/slices/chatSelection";
import { useDeleteChat } from "@chat/hooks/useDeleteChat";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";

interface ChatActionIconsProps {
    chatId: string;
    spacing?: number;
    size?: "large" | "medium" | "small";
    hideFavoriteButton?: boolean;
    hideDeleteButton?: boolean;
}

const ChatActionIcons = memo(({ chatId, spacing, size, hideDeleteButton, hideFavoriteButton }: ChatActionIconsProps) => {
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const deleteChat = useDeleteChat(chatId);

    const { data } = useQuery<Pick<Query, "chat">>(FETCH_CHAT, {
        variables: { id: chatId },
    });

    const chat = data?.chat;

    const [toggleFavoriteMutation] = useMutation<Pick<Mutation, "toggleChatFavorite">, { id: string }>(TOGGLE_FAVORITE, {
        variables: { id: chatId },
        refetchQueries: [{ query: FETCH_CHATS }],
    });

    const toggleFavorite = async (id: string) => {
        const result = await toggleFavoriteMutation({
            variables: { id: id as Scalars["ID"]["input"] },
            refetchQueries: [{ query: FETCH_CHATS }],
        });
        return result.data?.toggleChatFavorite;
    };

    const handleDeleteClick = () => {
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleConfirmDelete = () => {
        deleteChat();
        setOpenDeleteDialog(false);
    };

    // Enable Enter key for delete confirmation dialog
    useEnterKeyDialog({
        isOpen: openDeleteDialog,
        onConfirm: handleConfirmDelete,
    });

    return (
        <>
            <Grid2 container spacing={spacing ?? 0.75}>
                {!hideDeleteButton && (
                    <Grid2>
                        <IconButton size={size} onClick={handleDeleteClick} component="span">
                            <icon.delete sx={{ color: "semantic.icon.functional" }} />
                        </IconButton>
                    </Grid2>
                )}

                {!hideFavoriteButton && (
                    <Grid2>
                        <IconButton size={size} onClick={() => toggleFavorite(chatId)} component="span">
                            {chat?.isFavorite ? <icon.like sx={{ color: "semantic.icon.functional" }} /> : <icon.likeOff sx={{ color: "semantic.icon.functional" }} />}
                        </IconButton>
                    </Grid2>
                )}
            </Grid2>

            <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
                <DialogTitle>Chat löschen</DialogTitle>
                <DialogContent>Möchtest Du diesen Chat wirklich löschen?</DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={handleCloseDialog}>
                        Abbrechen
                    </Button>
                    <Button variant={"contained"} onClick={handleConfirmDelete} color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default ChatActionIcons;
